import React, { useState, useRef, useEffect, useCallback } from 'react';
import './FeaturedEventsSection.css';

const FeaturedEventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // API base URL for backend
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

  // Fetch featured events from backend API
  const fetchEvents = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      console.log('🎵 Fetching real concert data from backend API...');
      
      // Fetch from the main concerts endpoint which has all the scraped data
      console.log('🎵 Fetching from main concerts endpoint...');
      const response = await fetch(`${API_BASE_URL}/concerts`);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const apiData = await response.json();
      console.log('🔍 API Response:', apiData);
      
      // Handle different possible response structures
      let allEvents = [];
      if (Array.isArray(apiData.data)) {
        allEvents = apiData.data;
      } else if (apiData.data && Array.isArray(apiData.data.featured)) {
        allEvents = apiData.data.featured;
      } else if (apiData.data && Array.isArray(apiData.data.concerts)) {
        allEvents = apiData.data.concerts;
      } else if (Array.isArray(apiData)) {
        allEvents = apiData;
      } else if (apiData.events && Array.isArray(apiData.events)) {
        allEvents = apiData.events;
      } else {
        console.warn('⚠️ Unexpected API response structure:', apiData);
        allEvents = [];
      }
      
      console.log('✅ All events loaded:', allEvents.length);
      
      // Log all events to see what we have
      if (allEvents.length > 0) {
        console.log('🎵 All events sample:', allEvents.slice(0, 3).map(e => ({
          title: e.title,
          artist: e.artist,
          image: e.image,
          source: e.source
        })));
      }
      
      // Use all events - the scrapers already provide real images
      const finalEvents = allEvents;
      
      console.log('🎵 Final events loaded:', finalEvents.length);
      if (finalEvents.length > 0) {
        console.log('🎵 Sample images:', finalEvents.slice(0, 3).map(e => e.image));
      }
      
      setEvents(finalEvents.slice(0, 12)); // Limit to 12 events
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events');
      
      // Fallback to mock data only if something goes wrong
      setEvents([
        {
          id: 'fallback_1',
          artist: 'Fiona',
          title: 'We Are The Universe',
          date: '9.22 Mon @7pm',
          location: 'BBCB, Itaewon',
          likes: 57,
          rating: '10/10',
          image: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=300&h=200&fit=crop'
        },
        {
          id: 'fallback_2',
          artist: 'Alex',
          title: 'Neon Dreams',
          date: '9.25 Thu @8pm',
          location: 'Cakeshop, Hongdae',
          likes: 42,
          rating: '9/10',
          image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=200&fit=crop'
        },
        {
          id: 'fallback_3',
          artist: 'Maya',
          title: 'Electric Pulse',
          date: '9.26 Fri @9pm',
          location: 'UNDERCITY, Gangnam',
          likes: 38,
          rating: '9/10',
          image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=200&fit=crop'
        }
      ]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [API_BASE_URL]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Refresh function for manual data refresh
  const refreshEvents = () => {
    fetchEvents(false);
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef(null);
  const itemsPerView = 3;

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex + itemsPerView >= events.length ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? Math.max(0, events.length - itemsPerView) : prevIndex - 1
    );
  };

  // Drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(currentIndex);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed
    const newIndex = Math.max(0, Math.min(events.length - itemsPerView, scrollLeft - walk / 100));
    setCurrentIndex(Math.round(newIndex));
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - carouselRef.current.offsetLeft);
    setScrollLeft(currentIndex);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    const newIndex = Math.max(0, Math.min(events.length - itemsPerView, scrollLeft - walk / 100));
    setCurrentIndex(Math.round(newIndex));
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Show loading state
  if (loading) {
    return (
      <section className="featured-events">
        <div className="container">
          <div className="section-header">
            <h2>Featured Events</h2>
          </div>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading featured events...</p>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="featured-events">
        <div className="container">
          <div className="section-header">
            <h2>Featured Events</h2>
          </div>
          <div className="error-state">
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="featured-events">
      <div className="container">
        <div className="section-header">
          <h2>Featured Events</h2>
          <div className="header-actions">
            <button 
              className="refresh-btn" 
              onClick={refreshEvents}
              disabled={loading}
              title="Refresh events data"
            >
              <i className={`fas fa-sync-alt ${loading ? 'spinning' : ''}`}></i>
            </button>
            {lastUpdated && (
              <span className="last-updated">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        
        <div className="carousel-container">
          <button className="carousel-btn prev-btn" onClick={prevSlide}>
            <i className="fas fa-chevron-left"></i>
          </button>
          
          <div 
            className="events-carousel"
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="carousel-track" style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}>
              {console.log('🎵 Rendering events:', events)}
              {events.map((event) => (
                <div key={event.id} className="event-card">
                  <div className="event-image">
                    <img src={event.image} alt={event.title} />
                  </div>
                  <div className="event-info">
                    <h3 className="artist-name">{event.artist}</h3>
                    <h4 className="event-title">{event.title}</h4>
                    <div className="event-details">
                      <div className="event-detail">
                        <i className="fas fa-clock"></i>
                        <span>{event.date}</span>
                      </div>
                      <div className="event-detail">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{event.location}</span>
                      </div>
                      <div className="event-detail">
                        <i className="fas fa-heart"></i>
                        <span>{event.likes}</span>
                      </div>
                      <div className="event-detail">
                        <i className="fas fa-star"></i>
                        <span>{event.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button className="carousel-btn next-btn" onClick={nextSlide}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
        
        <div className="carousel-indicators">
          {Array.from({ length: Math.ceil(events.length / itemsPerView) }).map((_, index) => (
            <button
              key={index}
              className={`indicator ${Math.floor(currentIndex / itemsPerView) === index ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index * itemsPerView)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedEventsSection;